import {
  Row,
  Col,
  Card,
  Table,
  message,
  Button,
  Modal,
  Input,
  Flex,
  Avatar,
} from "antd";

import { PlusOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  addOrganizationMembers,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  updateOrganizationMemberRoles,
  deleteOrganizationMember,
  addOrganizationRobot,
} from "shared/services/organizations_services";
import { useAuth } from "shared/contexts/AuthProvider";
import { servicesUrlConfig } from "shared/config/setting";
import { useOrganizationData } from "features/administration/contexts/OrgsDataProvider";
import { useUserOrg } from "shared/contexts/UserOrgContext";
import { BUTTON_STYLES } from "../constants";
import {
  createOrgsColumns,
  createOrgMembersColumns,
  createOrgRobotColumns,
} from "../components/tableColumns";
import {
  AddOrganizationModal,
  EditOrganizationModal,
} from "../components/OrganizationModals";
import { AddMemberModal, EditMemberModal } from "../components/MemberModals";
import logger from "shared/utils/logging";

const MyOrganizationsPage = () => {
  const { t, ready } = useTranslation();

  const translate = (key) => {
    return ready ? t(key) : key;
  };

  const [selectedOrgRoles, setOrgRoles] = useState(null);

  const {
    myOrgsRoles,
    currentOrgDetail,
    loadMyOrganizationsRoles,
    dataLoading,
    getOrganizationsDetail,
    getOrgRobotsPaged,
    getOrgMembersPaged,
  } = useOrganizationData();

  const { auth, isSuperUser, loading: authLoading, authFetch } = useAuth();
  const { refreshUserOrgs } = useUserOrg();

  useEffect(() => {
    if (authLoading) return;
    loadMyOrganizationsRoles();
  }, [auth]);

  const memberData = currentOrgDetail?.members;
  const robotsData = currentOrgDetail?.robots;
  const { page, per_page, total, results } = myOrgsRoles || {
    page: 1,
    per_page: 10,
    total: 0,
    results: [],
  };

  const selectedOrgId = currentOrgDetail?.org_id || null;
  const selectedOrgMembers = currentOrgDetail?.members?.results || [];
  const selectedOrgRobots = currentOrgDetail?.robots?.results || [];

  const handleOrganizationSelection = (record) => {
    setOrgRoles(record.roles);
    const org = record.organization;
    logger.debug("Selected record: ", org);
    getOrganizationsDetail(org.org_id);
  };

  const handleMemberPageChange = (page, pageSize) => {
    getOrgMembersPaged(selectedOrgId, page, pageSize);
  };

  const handleRobotPageChange = (page, pageSize) => {
    getOrgRobotsPaged(selectedOrgId, page, pageSize);
  };

  const [isMemberModalVisible, setIsMemberModalVisible] = useState(false);
  const [isRobotModalVisible, setIsRobotModalVisible] = useState(false);
  const [newMember, setNewMember] = useState({ username: "", roles: [] });
  const [newRobot, setNewRobot] = useState({ robot_id: "" });
  const [isEditMemberModalVisible, setIsEditMemberModalVisible] =
    useState(false);
  const [editingMember, setEditingMember] = useState({
    username: "",
    roles: [],
  });
  const [isOrgModalVisible, setIsOrgModalVisible] = useState(false);
  const [isEditOrgModalVisible, setIsEditOrgModalVisible] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: "", description: "" });
  const [editingOrg, setEditingOrg] = useState({
    org_id: null,
    name: "",
    description: "",
    logo_path: null,
    logo_compact_path: null,
  });
  const [isOrgDetailModalVisible, setIsOrgDetailModalVisible] = useState(false);
  const [isRobotDetailModalVisible, setIsRobotDetailModalVisible] =
    useState(false);
  const [selectedRobotDetail, setSelectedRobotDetail] = useState(null);

  const isAdminOfSelectedOrg =
    selectedOrgRoles && selectedOrgRoles.includes("admin");

  const handleAddMember = () => {
    logger.debug("Adding member:", newMember);
    if (selectedOrgId === null) {
      message.error(translate("organizations.pleaseSelectOrganization"));
      return;
    }
    if (!newMember.username || newMember.roles.length === 0) {
      message.error(
        translate("organizations.pleaseEnterUsername") +
          " " +
          translate("organizations.pleaseSelectRoles"),
      );
      return;
    }

    addOrganizationMembers(
      auth.token,
      selectedOrgId,
      newMember.username,
      newMember.roles,
    )
      .then((response) => {
        logger.debug("Member added successfully:", response);
        message.success(translate("organizations.memberAddedSuccess"));
        setIsMemberModalVisible(false);
        setNewMember({ username: "", roles: [] });
        getOrganizationsDetail(selectedOrgId); // Refresh the organization details
      })
      .catch((error) => {
        logger.error("Error adding member:", error);
        message.error(
          error?.message || translate("organizations.memberAddFailed"),
        );
      });
  };

  const handleUpdateMemberRoles = () => {
    logger.debug("Updating member roles:", editingMember);
    if (selectedOrgId === null) {
      message.error(translate("organizations.pleaseSelectOrganization"));
      return;
    }
    if (!editingMember.username) {
      message.error(translate("organizations.pleaseSelectMember"));
      return;
    }

    updateOrganizationMemberRoles(
      auth.token,
      selectedOrgId,
      editingMember.username,
      editingMember.roles,
    )
      .then((response) => {
        logger.debug("Member roles updated successfully:", response);
        message.success(translate("organizations.memberUpdatedSuccess"));
        setIsEditMemberModalVisible(false);
        setEditingMember({ username: "", roles: [] });
        getOrganizationsDetail(selectedOrgId); // Refresh the organization details
      })
      .catch((error) => {
        logger.error("Error updating member roles:", error);
        message.error(
          error?.message || translate("organizations.memberUpdateFailed"),
        );
      });
  };

  const handleAddOrganization = () => {
    logger.debug("Adding organization:", newOrg);
    if (!newOrg.name || !newOrg.description) {
      message.error(translate("organizations.pleaseEnterOrgName"));
      return;
    }

    createOrganization(auth.token, newOrg.name, newOrg.description)
      .then((response) => {
        logger.debug("Organization added successfully:", response);
        message.success(translate("organizations.organizationCreatedSuccess"));
        setIsOrgModalVisible(false);
        setNewOrg({ name: "", description: "" });
        loadMyOrganizationsRoles(); // Refresh the organizations list
      })
      .catch((error) => {
        logger.error("Error adding organization:", error);
        message.error(
          error?.message || translate("organizations.organizationCreateFailed"),
        );
      });
  };

  const handleUpdateOrganization = () => {
    logger.debug("Updating organization:", editingOrg);
    if (!editingOrg.org_id || !editingOrg.name || !editingOrg.description) {
      message.error(translate("organizations.pleaseEnterOrgName"));
      return;
    }

    updateOrganization(
      auth.token,
      editingOrg.org_id,
      editingOrg.name,
      editingOrg.description,
    )
      .then((response) => {
        logger.debug("Organization updated successfully:", response);
        message.success(translate("organizations.organizationUpdatedSuccess"));
        setIsEditOrgModalVisible(false);
        setEditingOrg({ org_id: null, name: "", description: "" });
        loadMyOrganizationsRoles(); // Refresh the organizations list
      })
      .catch((error) => {
        logger.error("Error updating organization:", error);
        message.error(
          error?.message || translate("organizations.organizationUpdateFailed"),
        );
      });
  };

  const handleDeleteOrganization = (org) => {
    const org_id = org.org_id;
    logger.debug("Deleting organization:", org_id, org.name);
    Modal.confirm({
      title: translate("organizations.confirmDelete"),
      content: `${translate("organizations.organizationName")}: ${org.name}`,
      okText: translate("common.yes"),
      cancelText: translate("common.no"),
      onOk: () => {
        deleteOrganization(auth.token, org_id)
          .then((response) => {
            logger.debug("Organization deleted successfully:", response);
            message.success(
              translate("organizations.organizationDeletedSuccess"),
            );
            loadMyOrganizationsRoles(); // Refresh the organizations list
          })
          .catch((error) => {
            logger.error("Error deleting organization:", error);
            message.error(
              error?.message ||
                translate("organizations.organizationDeleteFailed"),
            );
          });
      },
    });
  };

  const handleAddRobot = () => {
    logger.debug("Adding robot:", newRobot);
    if (selectedOrgId === null) {
      message.error(translate("organizations.pleaseSelectOrganization"));
      return;
    }
    if (!newRobot.robot_id) {
      message.error(translate("organizations.pleaseEnterRobotId"));
      return;
    }

    addOrganizationRobot(auth.token, selectedOrgId, newRobot.robot_id)
      .then((response) => {
        logger.debug("Robot added successfully:", response);
        message.success(translate("organizations.robotAddedSuccess"));
        setIsRobotModalVisible(false);
        setNewRobot({ robot_id: "" });
        getOrganizationsDetail(selectedOrgId); // Refresh the organization details
      })
      .catch((error) => {
        logger.error("Error adding robot:", error);
        message.error(
          error?.message || translate("organizations.robotAddFailed"),
        );
      });
  };

  const handleRemoveMember = (username) => {
    logger.debug("Removing member:", username);
    if (selectedOrgId === null) {
      message.error(translate("organizations.pleaseSelectOrganization"));
      return;
    }

    Modal.confirm({
      title: translate("organizations.confirmRemoveMember"),
      content: `${translate("organizations.username")}: ${username}`,
      okText: translate("common.yes"),
      cancelText: translate("common.no"),
      onOk: () => {
        deleteOrganizationMember(auth.token, selectedOrgId, username)
          .then((response) => {
            message.success(translate("organizations.memberRemovedSuccess"));
            getOrganizationsDetail(selectedOrgId); // Refresh the organization details
          })
          .catch((error) => {
            logger.error("Error removing member:", error);
            message.error(
              error?.message || translate("organizations.memberRemoveFailed"),
            );
          });
      },
    });
  };

  const handleRemoveRobot = (robotId) => {
    if (selectedOrgId === null) {
      message.error(translate("organizations.pleaseSelectOrganization"));
      return;
    }

    Modal.confirm({
      title: translate("organizations.confirmRemoveRobot"),
      content: `${translate("organizations.robotId")}: ${robotId}`,
      okText: translate("common.yes"),
      cancelText: translate("common.no"),
      onOk: () => {
        const url = `${servicesUrlConfig.ORGS_SERVICE_URL}/${selectedOrgId}/robots?robot_id=${robotId}`;
        authFetch(url, {
          method: "DELETE",
          headers: {
            accept: "*/*",
            Authorization: `Bearer ${auth.token}`,
          },
        })
          .then((response) => {
            logger.debug("Robot removed successfully:", response);
            message.success(translate("organizations.robotRemovedSuccess"));
            getOrganizationsDetail(selectedOrgId);
          })
          .catch((error) => {
            logger.error("Error removing robot:", error);
            message.error(
              error.response?.data?.message ||
                error.message ||
                translate("organizations.robotRemoveFailed"),
            );
          });
      },
    });
  };

  const handleEditMember = (record) => {
    setEditingMember({ username: record?.user?.username, roles: record.roles });
    setIsEditMemberModalVisible(true);
  };

  const handleEditOrganization = (org) => {
    setEditingOrg({
      org_id: org.org_id,
      name: org.name,
      description: org.description,
      logo_path: org.logo_path ?? null,
      logo_compact_path: org.logo_compact_path ?? null,
    });
    setIsEditOrgModalVisible(true);
  };

  const handleOrgLogoUpload = async (file, logoType) => {
    if (!editingOrg.org_id) return;
    const formData = new FormData();
    formData.append("file", file);
    const url = logoType === "logo"
      ? servicesUrlConfig.ORGS_API.upload_logo(editingOrg.org_id)
      : servicesUrlConfig.ORGS_API.upload_logo_compact(editingOrg.org_id);
    try {
      const response = await authFetch(url, { method: "post", body: formData });
      const field = logoType === "logo" ? "logo_path" : "logo_compact_path";
      setEditingOrg((prev) => ({ ...prev, [field]: response.data[field] }));
      message.success("Logo uploaded successfully.");
      await refreshUserOrgs();
    } catch (err) {
      message.error(err?.response?.data?.detail || "Logo upload failed.");
    }
  };

  const handleOrgLogoDelete = async (logoType) => {
    if (!editingOrg.org_id) return;
    const url = logoType === "logo"
      ? servicesUrlConfig.ORGS_API.delete_logo(editingOrg.org_id)
      : servicesUrlConfig.ORGS_API.delete_logo_compact(editingOrg.org_id);
    try {
      await authFetch(url, { method: "delete" });
      const field = logoType === "logo" ? "logo_path" : "logo_compact_path";
      setEditingOrg((prev) => ({ ...prev, [field]: null }));
      message.success("Logo removed.");
      await refreshUserOrgs();
    } catch (err) {
      message.error(err?.response?.data?.detail || "Logo removal failed.");
    }
  };

  const handleRobotDoubleClick = (robotRecord) => {
    setSelectedRobotDetail(robotRecord);
    setIsRobotDetailModalVisible(true);
  };

  // Create table columns using shared factories
  const orgsColumns = createOrgsColumns(translate, {
    isMyOrgs: true,
    showActions: true,
    onEdit: handleEditOrganization,
    onDelete: handleDeleteOrganization,
  });

  const orgMembersColumns = createOrgMembersColumns(translate, {
    showActions: isAdminOfSelectedOrg,
    onEdit: handleEditMember,
    onDelete: handleRemoveMember,
  });

  const orgRobotColumns = createOrgRobotColumns(translate, {
    showActions: isAdminOfSelectedOrg,
    onDelete: handleRemoveRobot,
  });

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              variant="borderless"
              className="criclebox tablespace mb-24"
              title={translate("organizations.title")}
              // extra={
              //   <Button
              //     // type="primary"
              //     style={styleButtonAdd} // Ensure text stays within the button
              //     icon={<PlusOutlined />} // Add icon to the button
              //     onClick={() => setIsOrgModalVisible(true)}
              //     disabled={!isSuperUser}
              //   >
              //     New Organization
              //   </Button>
              // }
            >
              <div className="table-responsive">
                <Table
                  columns={orgsColumns}
                  dataSource={results}
                  rowKey={(record) => record.organization.org_id}
                  rowClassName={(record) =>
                    record.organization.org_id === selectedOrgId
                      ? "selected-row"
                      : ""
                  }
                  onRow={(record) => ({
                    onClick: () => handleOrganizationSelection(record),
                  })}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>
        <Row gutter={[24, 0]}>
          <Col xs={24} sm={24} md={12} lg={12} xl={14} className="mb-24">
            <Card
              variant="borderless"
              className="criclebox tablespace mb-24"
              title={translate("organizations.organizationMembers")}
              extra={
                <Button
                  icon={<PlusOutlined />}
                  style={BUTTON_STYLES.add}
                  onClick={() => setIsMemberModalVisible(true)}
                  disabled={!isAdminOfSelectedOrg}
                >
                  {translate("organizations.addMember")}
                </Button>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={[
                    ...orgMembersColumns,
                    {
                      // title: "Action",
                      key: "action",
                      align: "center",
                      render: (_, record) => (
                        <Flex justify="space-between" align="center">
                          <Button
                            type="link"
                            onClick={() => handleEditMember(record)}
                            disabled={!isAdminOfSelectedOrg}
                          >
                            {translate("organizations.edit")}
                          </Button>
                          <Button
                            type="link"
                            danger
                            onClick={() =>
                              handleRemoveMember(record?.user?.username)
                            }
                            disabled={!isAdminOfSelectedOrg}
                          >
                            {translate("organizations.remove")}
                          </Button>
                        </Flex>
                      ),
                    },
                  ]}
                  dataSource={selectedOrgMembers}
                  rowKey={(record) => record.user.username}
                  pagination={{
                    current: memberData?.page || 1,
                    pageSize: memberData?.per_page || 10,
                    total: memberData?.total || 0,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    onChange: handleMemberPageChange,
                  }}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={10} className="mb-24">
            <Card
              variant="borderless"
              className="criclebox tablespace mb-24"
              title={translate("organizations.organizationRobots")}
              extra={
                <Button
                  style={BUTTON_STYLES.add}
                  icon={<PlusOutlined />}
                  onClick={() => setIsRobotModalVisible(true)}
                  disabled={!isAdminOfSelectedOrg}
                >
                  {translate("organizations.addRobot")}
                </Button>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={[
                    ...orgRobotColumns,
                    // {
                    //   // title: "Action",
                    //   key: "action",
                    //   render: (_, record) => (
                    //     <Button
                    //       type="link"
                    //       danger
                    //       onClick={() => handleRemoveRobot(record.robot_id)}
                    //       disabled={!isAdminOfSelectedOrg}
                    //     >
                    //       {translate("organizations.remove")}
                    //     </Button>
                    //   ),
                    // },
                  ]}
                  dataSource={selectedOrgRobots}
                  rowKey="robot_id"
                  pagination={{
                    current: robotsData?.page || 1,
                    pageSize: robotsData?.per_page || 10,
                    total: robotsData?.total || 0,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    onChange: handleRobotPageChange,
                  }}
                  className="ant-border-space"
                  onRow={(record) => ({
                    onDoubleClick: () => handleRobotDoubleClick(record),
                  })}
                />
              </div>
            </Card>
            {/* </Card> */}
          </Col>
        </Row>
      </div>

      {/* Add Member Modal */}
      <AddMemberModal
        visible={isMemberModalVisible}
        member={newMember}
        onOk={handleAddMember}
        onCancel={() => setIsMemberModalVisible(false)}
        onChange={(updates) => setNewMember({ ...newMember, ...updates })}
        translate={translate}
      />

      {/* Edit Member Modal */}
      <EditMemberModal
        visible={isEditMemberModalVisible}
        member={editingMember}
        onOk={handleUpdateMemberRoles}
        onCancel={() => setIsEditMemberModalVisible(false)}
        onChange={(updates) =>
          setEditingMember({ ...editingMember, ...updates })
        }
        translate={translate}
      />

      {/* Add Robot Modal */}
      <Modal
        title={translate("organizations.addRobot")}
        open={isRobotModalVisible}
        onOk={handleAddRobot}
        onCancel={() => setIsRobotModalVisible(false)}
      >
        <Input
          placeholder={translate("organizations.robotId")}
          value={newRobot.robot_id}
          onChange={(e) =>
            setNewRobot({ ...newRobot, robot_id: e.target.value })
          }
        />
      </Modal>

      {/* Add Organization Modal */}
      <AddOrganizationModal
        visible={isOrgModalVisible}
        organization={newOrg}
        onOk={handleAddOrganization}
        onCancel={() => setIsOrgModalVisible(false)}
        onChange={(updates) => setNewOrg({ ...newOrg, ...updates })}
        translate={translate}
      />

      {/* Edit Organization Modal */}
      <EditOrganizationModal
        visible={isEditOrgModalVisible}
        organization={editingOrg}
        onOk={handleUpdateOrganization}
        onCancel={() => setIsEditOrgModalVisible(false)}
        onChange={(updates) => setEditingOrg({ ...editingOrg, ...updates })}
        onLogoUpload={handleOrgLogoUpload}
        onLogoDelete={handleOrgLogoDelete}
        canManageLogo={isAdminOfSelectedOrg || isSuperUser}
        canManageStorage={isAdminOfSelectedOrg || isSuperUser}
        translate={translate}
      />

      {/* Organization Details Modal */}
      {/* <Modal
        title={"Organization Details: " + currentOrgDetail?.name}
        open={isOrgDetailModalVisible}
        onCancel={() => setIsOrgDetailModalVisible(false)}
        footer={null}
        width={"95%"}
      >
        <Row gutter={[24, 0]}>
          <Col xs={24} sm={24} md={12} lg={12} xl={14} className="mb-24">
            <Card
              variant="borderless"
              className="criclebox tablespace mb-24"
              title="Organization Members"
              extra={
                <Button
                  icon={<PlusOutlined />} // Add icon to the button
                  style={styleButtonAdd} // Ensure text stays within the button
                  onClick={() => setIsMemberModalVisible(true)}
                >
                  Add Member
                </Button>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={[
                    ...orgMembersColumns,
                    {
                      title: "Action",
                      key: "action",
                      align: "center",
                      render: (_, record) => (
                        <Flex
                          justify="space-between"
                          align="center"
                        >
                          <Button
                            type="link"
                            onClick={() => handleEditMember(record)}
                          >
                            Edit
                          </Button>
                          <Button
                            type="link"
                            danger
                            onClick={() =>
                              handleRemoveMember(record?.user?.username)
                            }
                          >
                            Remove
                          </Button>
                        </Flex>
                      ),
                    },
                  ]}
                  dataSource={selectedOrgMembers}
                  rowKey={(record) => record.user.username}
                  pagination={false}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={10} className="mb-24">\n            <Card
              variant="borderless"
              className="criclebox tablespace mb-24"
              title="Organization Robots"
              extra={
                <Button
                  // type="primary"
                  style={styleButtonAdd}
                  icon={<PlusOutlined />} // Add icon to the button
                  onClick={() => setIsRobotModalVisible(true)}
                >
                  Add Robot
                </Button>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={[
                    ...orgRobotColumns,
                    {
                      title: "Action",
                      key: "action",
                      render: (_, record) => (
                        <Button
                          type="link"
                          danger
                          onClick={() => handleRemoveRobot(record.name)}
                        >
                          Remove
                        </Button>
                      ),
                    },
                  ]}
                  dataSource={selectedOrgRobots}
                  rowKey="robot_id"
                  pagination={false}
                  className="ant-border-space"
                />
              </div>
            </Card>
            
          </Col>
        </Row>
      </Modal> */}

      {/* Robot Details Modal */}
      <Modal
        title={`${translate("organizations.robotDetails")}: ${selectedRobotDetail?.robot_id || ""}`}
        open={isRobotDetailModalVisible}
        onCancel={() => setIsRobotDetailModalVisible(false)}
        footer={null}
      >
        <div style={{ padding: "20px", fontSize: "16px", lineHeight: "1.5" }}>
          <p>
            <strong>{translate("organizations.robotId")}:</strong>{" "}
            {selectedRobotDetail?.robot_id}
          </p>
          <p>
            <strong>{translate("organizations.name")}:</strong>{" "}
            {selectedRobotDetail?.name || translate("organizations.noname")}
          </p>
          <p>
            <strong>{translate("organizations.type")}:</strong>{" "}
            {selectedRobotDetail?.robot_type?.name ||
              translate("organizations.unknown")}
          </p>
          <p>
            <strong>{translate("organizations.apiKey")}:</strong>{" "}
            {selectedOrgRoles?.includes("admin")
              ? selectedRobotDetail?.api_key ||
                translate("organizations.notAvailable")
              : translate("organizations.notAllowedToView")}
          </p>
          <p>
            <strong>{translate("organizations.avatar")}:</strong>
          </p>
          {selectedRobotDetail?.avatar ? (
            <Avatar
              className="shape-avatar"
              shape="square"
              size={80}
              src={selectedRobotDetail.avatar}
            />
          ) : (
            <span>{translate("organizations.noAvatarAvailable")}</span>
          )}
        </div>
      </Modal>
    </>
  );
};

// Add CSS for highlighting the selected row
const styles = `
  .selected-row {
    background-color: #e6f7ff !important;
  }
`;
document.head.insertAdjacentHTML("beforeend", `<style>${styles}</style>`);

export default MyOrganizationsPage;
